import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import HeroCanvas from "./HeroCanvas";
import GenesisCountdown from "./GenesisCountdown";
import CoinHero from "./CoinHero";
import { useLaunchModal } from "./LaunchModal";
import { useTickMarkets } from "@/lib/market";
import { scrollToId } from "@/lib/scroll";
import { CountUp } from "./Reveal";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { delay: 0.25 + i * 0.13, duration: 1, ease: [0.22, 1, 0.36, 1] },
    }),
};

export default function Hero() {
    const quotes = useTickMarkets();
    const { open } = useLaunchModal();
    const ref = useRef(null);

    useEffect(() => {
        const v = document.querySelector("[data-testid='hero-video-bg']");
        if (!v) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            v.pause();
            return;
        }
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
    }, []);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const mockY = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, 120]);

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden bg-base"
        >
            {/* cinematic looped Earth background — original render, poster fallback on mobile */}
            <motion.video
                data-testid="hero-video-bg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={`${process.env.PUBLIC_URL}/brand/hero-video-poster.jpg`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.8, delay: 0.2 }}
                className="absolute inset-0 hidden h-full w-full object-cover opacity-75 md:block"
            >
                <source src={`${process.env.PUBLIC_URL}/brand/tavonx-hero-loop.webm`} type="video/webm" />
                <source src={`${process.env.PUBLIC_URL}/brand/tavonx-hero-loop.mp4`} type="video/mp4" />
            </motion.video>
            <img
                src={`${process.env.PUBLIC_URL}/brand/hero-video-poster.jpg`}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover opacity-75 md:hidden"
            />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(5,7,12,0.95)_0%,rgba(5,7,12,0.74)_40%,rgba(5,7,12,0.34)_74%,rgba(5,7,12,0.52)_100%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,7,12,0.55)_0%,rgba(5,7,12,0.12)_30%,rgba(5,7,12,0.1)_70%,rgba(5,7,12,0.92)_100%)]" />
            <div className="absolute inset-0 bg-grid bg-grid-fade animate-grid-pan opacity-35" />
            <motion.div style={{ y: glowY }} className="absolute inset-0" aria-hidden="true">
                <div className="absolute left-1/2 top-[-20%] h-[60vh] w-[90vw] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(0,209,255,0.13),rgba(37,99,235,0.07)_45%,transparent_70%)]" />
                <div className="absolute right-[-10%] top-[30%] h-[50vh] w-[40vw] rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.08),transparent_70%)]" />
            </motion.div>
            <HeroCanvas />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-void" />

            <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-32 sm:px-8 lg:px-12 lg:pb-28 lg:pt-36">
                <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
                    {/* left — headline */}
                    <motion.div style={{ y: textY }} className="lg:col-span-6">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 backdrop-blur"
                        >
                            <span className="h-1.5 w-1.5 animate-blink rounded-full bg-cyber" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-300">
                                Your gateway to global digital markets
                            </span>
                        </motion.div>

                        <h1
                            data-testid="hero-title"
                            className="font-grotesk text-4xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl"
                        >
                            {["Trade", "Beyond"].map((w, i) => (
                                <span className="mask-line" key={w}>
                                    <motion.span
                                        custom={i}
                                        variants={line}
                                        initial="hidden"
                                        animate="show"
                                    >
                                        {w}
                                    </motion.span>
                                </span>
                            ))}
                            <span className="mask-line">
                                <motion.span custom={2} variants={line} initial="hidden" animate="show" className="text-grad-blue">
                                    Limits.
                                </motion.span>
                            </span>
                        </h1>

                        <motion.p
                            data-testid="hero-subtitle"
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.85 }}
                            className="mt-7 max-w-md text-base leading-relaxed text-slate-400 sm:text-lg"
                        >
                            Trade digital assets through a next-generation exchange
                            built for speed, security and a connected global market.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1 }}
                            className="mt-10 flex flex-col gap-3.5 sm:flex-row sm:items-center"
                        >
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    open("trading");
                                }}
                                data-testid="hero-cta-start-trading"
                                className="group inline-flex items-center justify-center gap-2 rounded bg-gradient-to-r from-electric to-cyber px-8 py-4 font-grotesk text-sm font-semibold uppercase tracking-[0.18em] text-void transition-all duration-300 hover:shadow-[0_0_36px_rgba(0,209,255,0.35)]"
                            >
                                Start Trading
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </a>
                            <a
                                href="#markets"
                                onClick={scrollToId("markets")}
                                data-testid="hero-cta-explore-markets"
                                className="inline-flex items-center justify-center gap-2 rounded border border-white/15 px-8 py-4 font-grotesk text-sm font-semibold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:border-cyber/60 hover:text-cyber"
                            >
                                Explore Markets
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.1 }}
                            className="mt-9"
                        >
                            <GenesisCountdown />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 1.25 }}
                            className="mt-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500"
                        >
                            <span>Genesis Launch · 28 Oct 2026</span>
                            <span className="h-3 w-px bg-white/15" />
                            <span className="rounded border border-white/10 px-2 py-0.5 text-cyan-300/80">
                                TNX · Utility Token
                            </span>
                        </motion.div>
                    </motion.div>

                    {/* right — premium TNX coin hero visual */}
                    <CoinHero quotes={quotes} />
                </div>
            </div>
        </section>
    );
}
